import { mapSelbstbeteiligungToDropdownDisplayValue } from '@ps20-frontend/utils';
import { Selbstbeteiligung } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { CoverageType, VertragsoptionenTKV } from '../models/vertragsoptionen.model';

const VERKAUFSANGEBOT_TO_PRODUCT_NAME_MAPPING: Record<string, string> = {
  TM009: 'Vollschutz Basis',
  TM013: 'Vollschutz Basis',
  TM017: 'Vollschutz Basis',

  TM010: 'Vollschutz Smart',
  TM014: 'Vollschutz Smart',
  TM018: 'Vollschutz Smart',

  TM011: 'Vollschutz Komfort',
  TM015: 'Vollschutz Komfort',
  TM019: 'Vollschutz Komfort',

  TM012: 'Vollschutz Premium',
  TM016: 'Vollschutz Premium',
  TM020: 'Vollschutz Premium',

  TM021: 'OP-Schutz Basis',
  TM025: 'OP-Schutz Basis',
  TM029: 'OP-Schutz Basis',

  TM022: 'OP-Schutz Smart',
  TM026: 'OP-Schutz Smart',
  TM030: 'OP-Schutz Smart',

  TM023: 'OP-Schutz Komfort',
  TM027: 'OP-Schutz Komfort',
  TM031: 'OP-Schutz Komfort',

  TM024: 'OP-Schutz Premium',
  TM028: 'OP-Schutz Premium',
  TM032: 'OP-Schutz Premium',
};

export const selectVertragsoptionen = (state: IAppState): VertragsoptionenTKV =>
  state.vertragsoptionen;

export const selectCoverageType = (state: IAppState): CoverageType => {
  return state.vertragsoptionen.coverageType;
};

export const selectHasPreinsuredWechselbaustein = (state: IAppState): boolean => {
  return state.vertragsoptionen.hasWechselbaustein;
};

export const selectZahlweise = (state: IAppState): string =>
  state.vertragsoptionen.zahlungsweise.toLowerCase();

export const selectIsNeugeschaeft = (state: IAppState): boolean =>
  !state.vertragsoptionen.vorvertragsNummern ||
  state.vertragsoptionen.vorvertragsNummern.length === 0;

export const selectProductName = (state: IAppState): string => {
  const verkaufsangebotKurzname =
    state.vertragsoptionen.vorvertragsNummern?.[0].verkaufsangebotKurzname;
  return (
    VERKAUFSANGEBOT_TO_PRODUCT_NAME_MAPPING[verkaufsangebotKurzname] || verkaufsangebotKurzname
  );
};

export const selectIsTkvV2x = (state: IAppState): boolean => {
  const verkaufsangebotKurzname = state.vertragsoptionen.verkaufsangebotKurzname;
  return verkaufsangebotKurzname in VERKAUFSANGEBOT_TO_PRODUCT_NAME_MAPPING;
};

export const selectSelbstbeteiligung = (state: IAppState): string =>
  mapSelbstbeteiligungToDropdownDisplayValue(state.vertragsoptionen.selbstbeteiligung);

export const selectHasWechselbaustein = (state: IAppState): boolean =>
  state.vertragsoptionen.hasWechselbaustein;

export const selectAllPossibleSelbstbeteiligungen = (): Array<Selbstbeteiligung> => [
  Selbstbeteiligung.SELBSTBETEILIGUNG_0000,
  Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT,
  Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
];

export const selectIsTkv20 = (state: IAppState): boolean => {
  const preContractPreisverzeichnis = state.vertragsoptionen.vorvertragsNummern?.[0]?.vorbelegungForBeibehaltung?.preisverzeichnis;
  return preContractPreisverzeichnis != null && preContractPreisverzeichnis < 100;
};

export const selectPossibleSelbstbeteiligungen = (state: IAppState): Array<Selbstbeteiligung> => {
  if (selectIsTkv20(state) || state.versicherungsnehmer.wantsCoverageUpgrade === true) {
    return selectAllPossibleSelbstbeteiligungen();
  }
  switch (state.vertragsoptionen.vorvertragsNummern?.[0]?.selbstbeteiligung) {
    case Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT: {
      return [
        Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT,
        Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT,
      ];
    }
    case Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT: {
      return [Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT];
    }
    default:
      return selectAllPossibleSelbstbeteiligungen();
  }
};

export const selectVorvertragKurzname = (state: IAppState): string => {
  return state.vertragsoptionen.vorvertragsNummern?.[0].verkaufsangebotKurzname;
};

export const selectShouldShowIllnessSection = (state: IAppState): boolean => {
  const isNeugeschaeft = selectIsNeugeschaeft(state);
  const wantsCoverageUpgrade = state.versicherungsnehmer.wantsCoverageUpgrade;
  const hasWechselbaustein = selectHasWechselbaustein(state);
  const useExistingWechselbaustein = state.versicherungsnehmer.useExistingWechselbaustein;

  return (
    isNeugeschaeft ||
    (wantsCoverageUpgrade && (!hasWechselbaustein || useExistingWechselbaustein === false))
  );
};
